import axios from 'axios';
import i18n from 'i18next';
import { format } from 'date-fns';

// In production (Docker), use relative path as Nginx handles the proxy
// In development, use the full URL
const BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/shift-tracker'
  : process.env.REACT_APP_API_URL || 'http://localhost:8080/shift-tracker';

// Create axios instance with base configuration
const createApiInstance = () => {
  const token = localStorage.getItem('token');
  
  const instance = axios.create({
    baseURL: BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      'Accept-Language': i18n.language // Add language header
    },
  });

  // Add response interceptor to handle 401 errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && localStorage.getItem('token')) {
        // Clear token if we get an unauthorized error
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Get a fresh API instance for each request
const getApi = () => createApiInstance();

export const authApi = {
  login: (email, password) => 
    getApi().post('/auth/login', { email, password }),
  
  register: (username, email, password) =>
    getApi().post('/auth/register', { username, email, password }),
    
  forgotPassword: (email) =>
    getApi().post('/auth/forgot-password', { email }),

  changePassword: (currentPassword, newPassword) =>
    getApi().post('/auth/change-password', { currentPassword, newPassword }),
};

export const shiftsApi = {
  getShifts: (page, search, startDate, endDate, personFilter, companyFilter, checkInLocationFilter, checkOutLocationFilter, sortBy, sortDirection, activeShifts) => {
    // Create Date objects and format them in Romanian style
    const adjustedStartDate = startDate ? new Date(startDate) : new Date();
    const adjustedEndDate = endDate ? new Date(endDate) : new Date();
    
    const params = {
      page,
      startDate: format(adjustedStartDate, 'dd.MM.yyyy'),
      endDate: format(adjustedEndDate, 'dd.MM.yyyy')
    };

    // Only add search if it exists and is not empty
    if (search && search.trim()) {
      params.search = search;
    }
    
    // Add sorting parameters if they exist
    if (sortBy && sortBy.trim()) {
      params.sortBy = sortBy;
      if (sortDirection) {
        params.sortDirection = sortDirection;
      }
    }
    
    // Add active shifts parameter if true
    if (activeShifts) {
      params.activeShifts = true;
    }
    
    // Add column filters if they exist and are not empty
    if (personFilter && personFilter.trim()) {
      // Remove any empty values that might have been included
      const filteredPersons = personFilter.split(',').filter(Boolean);
      console.log('Person filter values:', filteredPersons);
      if (filteredPersons.length > 0) {
        // Use encodeURIComponent for each value to ensure proper URL encoding
        params.personFilter = filteredPersons.map(p => encodeURIComponent(p)).join(',');
      }
    }
    
    if (companyFilter && companyFilter.trim()) {
      // Remove any empty values that might have been included
      const filteredCompanies = companyFilter.split(',').filter(Boolean);
      console.log('Company filter values:', filteredCompanies);
      if (filteredCompanies.length > 0) {
        // Use encodeURIComponent for each value to ensure proper URL encoding
        params.companyFilter = filteredCompanies.map(c => encodeURIComponent(c)).join(',');
      }
    }
    
    if (checkInLocationFilter && checkInLocationFilter.trim()) {
      // Remove any empty values that might have been included
      const filteredCheckInLocations = checkInLocationFilter.split(',').filter(Boolean);
      console.log('Check-in location filter values:', filteredCheckInLocations);
      if (filteredCheckInLocations.length > 0) {
        // Use encodeURIComponent for each value to ensure proper URL encoding
        params.checkInLocationFilter = filteredCheckInLocations.map(l => encodeURIComponent(l)).join(',');
      }
    }
    
    if (checkOutLocationFilter && checkOutLocationFilter.trim()) {
      // Remove any empty values that might have been included
      const filteredCheckOutLocations = checkOutLocationFilter.split(',').filter(Boolean);
      console.log('Check-out location filter values:', filteredCheckOutLocations);
      if (filteredCheckOutLocations.length > 0) {
        // Use encodeURIComponent for each value to ensure proper URL encoding
        params.checkOutLocationFilter = filteredCheckOutLocations.map(l => encodeURIComponent(l)).join(',');
      }
    }
    
    console.log('Sending filter params to backend:', params);
    return getApi().get('/shifts', { params });
  },
  
  getFilterOptions: (startDate, endDate) => {
    // Create Date objects and format them in Romanian style
    const adjustedStartDate = startDate ? new Date(startDate) : new Date();
    const adjustedEndDate = endDate ? new Date(endDate) : new Date();
    
    return getApi().get('/shifts/filter-options', {
      params: {
        startDate: format(adjustedStartDate, 'dd.MM.yyyy'),
        endDate: format(adjustedEndDate, 'dd.MM.yyyy')
      }
    });
  },
  
  clockInOut: (rfidTagId) =>
    getApi().post(`/shifts/clock/${rfidTagId}`),

  getEmployees: () =>
    getApi().get('/shifts/employees'),
    
  getEmployeesByDateRange: (startDate, endDate) =>
    getApi().get('/shifts/employees-by-date-range', {
      params: {
        startDate: format(new Date(startDate), 'dd.MM.yyyy'),
        endDate: format(new Date(endDate), 'dd.MM.yyyy')
      }
    }),
  
  generateCompleteReport: (startDate, endDate, language = 'ro') =>
    getApi().get('/shifts/report/complete', { 
      params: { 
        startDate,
        endDate,
        language 
      },
      responseType: 'blob'
    }),

  generateEmployeeReport: (startDate, endDate, employeeName, language = 'ro') =>
    getApi().get('/shifts/report/employee', { 
      params: { 
        startDate,
        endDate,
        employeeName,
        language 
      },
      responseType: 'blob'
    }),
};

export const userApi = {
    getUsers: () => 
        getApi().get('/api/users'),
    
    createUser: (userData) =>
        getApi().post('/api/users', userData),
    
    updateUser: (userId, userData) =>
        getApi().put(`/api/users/${userId}`, userData),
    
    deleteUser: (userId) =>
        getApi().delete(`/api/users/${userId}`),
};

export const featureFlagsApi = {
  getFeatureFlags: () => 
    getApi().get('/api/feature-flags'),
  
  updateFeatureFlag: (featureName, enabled) =>
    getApi().put(`/api/feature-flags/${featureName}`, { enabled }),
};

export default getApi();